import { useEffect, useMemo, useRef, useState } from "react";
import { useSettings } from "./useSettings.jsx";
import ThemePicker from "../components/ThemePicker.jsx";
import "./settings.css";

// ── Declarative schema ────────────────────────────────────────────────
// Every control is described once here; the UI, the search filter and the
// wiring to setSetting are all derived from this. `key` uses dotted paths for
// nested values (e.g. "units.altitude").
const CATEGORIES = [
  {
    id: "appearance",
    label: "Appearance",
    fields: [
      {
        key: "theme",
        label: "Colour theme",
        help: "Pick a colour palette; the map basemap follows the theme.",
        type: "custom",
        slot: "theme",
      },
      {
        key: "density",
        label: "Layout density",
        help: "Compact fits more on screen; comfortable adds breathing room.",
        type: "select",
        options: [
          { value: "comfortable", label: "Comfortable" },
          { value: "compact", label: "Compact" },
        ],
      },
      {
        key: "fontScale",
        label: "Text size",
        help: "Scale all text up or down for easier reading.",
        type: "range",
        min: 0.8,
        max: 1.6,
        step: 0.1,
        format: (v) => `${Math.round(v * 100)}%`,
      },
      {
        key: "reduceMotion",
        label: "Reduce motion",
        help: "Minimise animations and transitions across the app.",
        type: "toggle",
      },
    ],
  },
  {
    id: "map",
    label: "Map & data",
    fields: [
      {
        key: "showLabels",
        label: "Show aircraft labels",
        help: "Display callsign labels next to planes on the map.",
        type: "toggle",
      },
      {
        key: "cluster",
        label: "Cluster nearby aircraft",
        help: "Group crowded planes into clusters when zoomed out.",
        type: "toggle",
      },
      {
        key: "dataSource",
        label: "Data source",
        help: "Where live positions come from. Auto picks the best available.",
        type: "select",
        options: [
          { value: "auto", label: "Auto" },
          { value: "opensky", label: "OpenSky" },
          { value: "adsb", label: "adsb.lol" },
        ],
      },
      {
        key: "refreshMs",
        label: "Refresh interval",
        help: "How often to fetch new positions. Lower uses more data.",
        type: "select",
        parse: Number,
        options: [
          { value: 5000, label: "Every 5 seconds" },
          { value: 10000, label: "Every 10 seconds" },
          { value: 15000, label: "Every 15 seconds" },
          { value: 30000, label: "Every 30 seconds" },
          { value: 60000, label: "Every minute" },
        ],
      },
    ],
  },
  {
    id: "preview",
    label: "3D preview",
    fields: [
      {
        key: "preview3d",
        label: "3D cockpit preview",
        help: "Show the “Preview flight in 3D” button. The 3D engine (Cesium) loads only the first time you open a preview.",
        type: "toggle",
      },
      {
        key: "previewTerrain",
        label: "Real world terrain",
        help: "Render mountains and coastlines out the window. Requires a free Cesium ion token; otherwise a smooth globe is used.",
        type: "toggle",
      },
    ],
  },
  {
    id: "alerts",
    label: "Alerts",
    fields: [
      {
        key: "emergencySound",
        label: "Emergency sound",
        help: "Play a sound when an aircraft squawks an emergency code.",
        type: "toggle",
      },
    ],
  },
  {
    id: "units",
    label: "Units",
    fields: [
      {
        key: "units.altitude",
        label: "Altitude",
        help: "Units for aircraft altitude readouts.",
        type: "select",
        options: [
          { value: "ft", label: "Feet (ft)" },
          { value: "m", label: "Metres (m)" },
        ],
      },
      {
        key: "units.speed",
        label: "Speed",
        help: "Units for aircraft ground speed.",
        type: "select",
        options: [
          { value: "kn", label: "Knots (kn)" },
          { value: "mph", label: "Miles per hour (mph)" },
          { value: "kmh", label: "Kilometres per hour (km/h)" },
        ],
      },
      {
        key: "units.temp",
        label: "Temperature",
        help: "Units for weather temperatures.",
        type: "select",
        options: [
          { value: "C", label: "Celsius (°C)" },
          { value: "F", label: "Fahrenheit (°F)" },
        ],
      },
      {
        key: "units.time",
        label: "Time zone",
        help: "Show times in your local zone or coordinated universal time.",
        type: "select",
        options: [
          { value: "local", label: "Local time" },
          { value: "utc", label: "UTC" },
        ],
      },
    ],
  },
  {
    id: "privacy",
    label: "Privacy & about",
    fields: [
      {
        key: "__about",
        label: "About Plane Spotter",
        help: "Settings are stored only in this browser (localStorage). Nothing is uploaded. Live data comes from OpenSky / adsb.lol.",
        type: "info",
      },
    ],
  },
];

// Read a possibly-nested value out of the settings object.
function getValue(settings, key) {
  if (key.includes(".")) {
    const [parent, child] = key.split(".");
    return settings[parent]?.[child];
  }
  return settings[key];
}

function fieldMatches(field, query) {
  if (!query) return true;
  const q = query.toLowerCase();
  return (
    field.label.toLowerCase().includes(q) ||
    (field.help || "").toLowerCase().includes(q)
  );
}

// ── A single settings row ─────────────────────────────────────────────
function SettingRow({ field, slots }) {
  const { settings, setSetting } = useSettings();
  const value = getValue(settings, field.key);
  const controlId = `set-${field.key.replace(/\./g, "-")}`;
  const helpId = `${controlId}-help`;

  function commit(raw) {
    const parsed = field.parse ? field.parse(raw) : raw;
    setSetting(field.key, parsed);
  }

  let control = null;
  if (field.type === "custom") {
    control = slots?.[field.slot] ?? null;
  } else if (field.type === "select") {
    control = (
      <select
        id={controlId}
        className="set-select"
        aria-describedby={helpId}
        value={String(value)}
        onChange={(e) => {
          // Recover the original option value type (number vs string).
          const opt = field.options.find(
            (o) => String(o.value) === e.target.value
          );
          commit(opt ? opt.value : e.target.value);
        }}
      >
        {field.options.map((o) => (
          <option key={String(o.value)} value={String(o.value)}>
            {o.label}
          </option>
        ))}
      </select>
    );
  } else if (field.type === "toggle") {
    control = (
      <button
        id={controlId}
        type="button"
        role="switch"
        aria-checked={!!value}
        aria-describedby={helpId}
        className={value ? "set-switch on" : "set-switch"}
        onClick={() => setSetting(field.key, !value)}
      >
        <span className="set-switch-knob" aria-hidden="true" />
        <span className="set-switch-text">{value ? "On" : "Off"}</span>
      </button>
    );
  } else if (field.type === "range") {
    control = (
      <div className="set-range-wrap">
        <input
          id={controlId}
          type="range"
          className="set-range"
          aria-describedby={helpId}
          min={field.min}
          max={field.max}
          step={field.step}
          value={value}
          onChange={(e) => setSetting(field.key, Number(e.target.value))}
        />
        <output className="set-range-value">
          {field.format ? field.format(value) : value}
        </output>
      </div>
    );
  }

  if (field.type === "info") {
    return (
      <div className="set-row set-row-info">
        <h4 className="set-info-title">{field.label}</h4>
        <p id={helpId} className="set-help">
          {field.help}
        </p>
      </div>
    );
  }

  return (
    <div className="set-row">
      <div className="set-row-text">
        <label className="set-label" htmlFor={controlId}>
          {field.label}
        </label>
        <p id={helpId} className="set-help">
          {field.help}
        </p>
      </div>
      <div className="set-control">{control}</div>
    </div>
  );
}

// ── The panel ─────────────────────────────────────────────────────────
export default function SettingsPanel({ open, onClose, themeId, onThemeChange }) {
  const { resetSettings, importSettings, exportSettings } = useSettings();

  // Slots for `custom` fields — controls whose state lives outside the
  // settings store (e.g. the theme, which App owns and persists separately).
  const slots = useMemo(
    () => ({
      theme: <ThemePicker themeId={themeId} onChange={onThemeChange} />,
    }),
    [themeId, onThemeChange]
  );
  const [activeId, setActiveId] = useState(CATEGORIES[0].id);
  const [query, setQuery] = useState("");
  const [importError, setImportError] = useState("");
  const dialogRef = useRef(null);
  const searchRef = useRef(null);
  const fileRef = useRef(null);

  // Esc to close + move focus in on open.
  useEffect(() => {
    if (!open) return;
    function onKeyDown(e) {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    }
    document.addEventListener("keydown", onKeyDown);
    // Focus the search field so keyboard users land somewhere useful.
    const t = setTimeout(() => searchRef.current?.focus(), 0);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      clearTimeout(t);
    };
  }, [open, onClose]);

  const searching = query.trim().length > 0;

  // When searching, show every category that has a matching field, flattened.
  const visibleCategories = useMemo(() => {
    if (!searching) {
      return CATEGORIES.map((c) => ({ ...c }));
    }
    return CATEGORIES.map((c) => ({
      ...c,
      fields: c.fields.filter((f) => fieldMatches(f, query)),
    })).filter((c) => c.fields.length > 0);
  }, [query, searching]);

  const active = CATEGORIES.find((c) => c.id === activeId) || CATEGORIES[0];

  if (!open) return null;

  function onBackdropClick(e) {
    if (e.target === e.currentTarget) onClose();
  }

  function handleExport() {
    const blob = new Blob([exportSettings()], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "plane-spotter-settings.json";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  function handleImportFile(e) {
    setImportError("");
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        importSettings(String(reader.result));
      } catch (err) {
        setImportError(err.message || "Could not import that file.");
      }
    };
    reader.onerror = () => setImportError("Could not read that file.");
    reader.readAsText(file);
    e.target.value = ""; // allow re-importing the same file
  }

  return (
    <div className="settings-backdrop" onClick={onBackdropClick}>
      <div
        className="settings-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="settings-title"
        tabIndex={-1}
        ref={dialogRef}
      >
        <header className="settings-head">
          <h2 id="settings-title" className="settings-title">
            Settings
          </h2>
          <button
            type="button"
            className="settings-close"
            onClick={onClose}
            aria-label="Close settings"
          >
            ✕
          </button>
        </header>

        <div className="settings-search-wrap">
          <input
            ref={searchRef}
            type="search"
            className="settings-search"
            placeholder="Search settings…"
            aria-label="Search settings"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        <div className="settings-body">
          {!searching && (
            <nav
              className="settings-tabs"
              role="tablist"
              aria-label="Settings categories"
              aria-orientation="vertical"
            >
              {CATEGORIES.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  role="tab"
                  id={`tab-${c.id}`}
                  aria-selected={c.id === activeId}
                  aria-controls={`panel-${c.id}`}
                  tabIndex={c.id === activeId ? 0 : -1}
                  className={
                    c.id === activeId ? "settings-tab active" : "settings-tab"
                  }
                  onClick={() => setActiveId(c.id)}
                >
                  {c.label}
                </button>
              ))}
            </nav>
          )}

          <div className="settings-content">
            {searching ? (
              visibleCategories.length === 0 ? (
                <p className="settings-empty">
                  No settings match “{query.trim()}”.
                </p>
              ) : (
                visibleCategories.map((c) => (
                  <section key={c.id} className="settings-group">
                    <h3 className="settings-group-title">{c.label}</h3>
                    {c.fields.map((f) => (
                      <SettingRow key={f.key} field={f} slots={slots} />
                    ))}
                  </section>
                ))
              )
            ) : (
              <section
                role="tabpanel"
                id={`panel-${active.id}`}
                aria-labelledby={`tab-${active.id}`}
                className="settings-group"
              >
                <h3 className="settings-group-title">{active.label}</h3>
                {active.fields.map((f) => (
                  <SettingRow key={f.key} field={f} slots={slots} />
                ))}
              </section>
            )}
          </div>
        </div>

        <footer className="settings-foot">
          {importError ? (
            <p className="settings-error" role="alert">
              {importError}
            </p>
          ) : null}
          <div className="settings-foot-buttons">
            <button
              type="button"
              className="settings-btn"
              onClick={() => {
                if (
                  window.confirm("Reset all settings to their defaults?")
                ) {
                  resetSettings();
                }
              }}
            >
              Reset to defaults
            </button>
            <button
              type="button"
              className="settings-btn"
              onClick={handleExport}
            >
              Export
            </button>
            <button
              type="button"
              className="settings-btn"
              onClick={() => fileRef.current?.click()}
            >
              Import
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="application/json,.json"
              className="settings-file"
              aria-label="Import settings file"
              onChange={handleImportFile}
            />
          </div>
        </footer>
      </div>
    </div>
  );
}
